import { useCallback, useEffect } from 'react';
import { ArrowDown, ArrowUp, FileClock, FilePenLine, RefreshCw, ShieldCheck } from 'lucide-react';
import { CATEGORY_LABELS } from '../../../../types/processBlueprint';
import { useBlueprintGridQuery } from '../../hooks/useBlueprintGridQuery';
import { useExcelenciaFiltersStore } from '../../state/excelenciaFilters.store';
import { BlueprintStatusBadge } from '../shared/BlueprintStatusBadge';
import type { BlueprintListItem } from '../../services/blueprint.service';

interface BlueprintGridProps {
  organizationId: string;
  onSelect: (item: BlueprintListItem) => void;
  selectedId?: string | null;
  refreshToken?: number;
  showFilters?: boolean;
  onItemsChange?: (items: BlueprintListItem[]) => void;
  onEdit?: (item: BlueprintListItem) => void;
  onOpenHistory?: (item: BlueprintListItem) => void;
  onOpenAudit?: (item: BlueprintListItem) => void;
}

const COLUMNS: Array<{
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: 'name' | 'category' | 'status' | 'semver' | 'updated_at' | 'usage';
}> = [
  { key: 'name', label: 'NOME', sortable: true, sortKey: 'name' },
  { key: 'semver', label: 'VERSAO', sortable: true, sortKey: 'semver' },
  { key: 'category', label: 'TIPO', sortable: true, sortKey: 'category' },
  { key: 'status', label: 'STATUS', sortable: true, sortKey: 'status' },
  { key: 'usage', label: 'EM USO', sortable: true, sortKey: 'usage' },
  { key: 'createdBy', label: 'CRIADO POR' },
  { key: 'updatedAt', label: 'ATUALIZADO', sortable: true, sortKey: 'updated_at' },
  { key: 'actions', label: 'ACOES' },
];

function formatCategory(value?: string | null) {
  if (!value) return '-';
  return CATEGORY_LABELS[value] ?? value;
}

function formatAuthor(item: BlueprintListItem) {
  if (item.createdByName) return item.createdByName;
  if (item.createdBy) return item.createdBy.slice(0, 8);
  return 'Sistema';
}

function formatUpdatedAt(item: BlueprintListItem) {
  const updatedAt = (item as unknown as { updated_at?: string }).updated_at ?? item.updatedAt;
  return updatedAt ? new Date(updatedAt).toLocaleString('pt-BR') : '-';
}

export function BlueprintGrid({
  organizationId,
  onSelect,
  selectedId = null,
  refreshToken = 0,
  showFilters = true,
  onItemsChange,
  onEdit,
  onOpenHistory,
  onOpenAudit,
}: BlueprintGridProps) {
  const {
    setOrganization,
    setPage,
    setSort,
    setStatus,
    setCategory,
    setSearch,
    page,
    pageSize,
    sortKey,
    sortDir,
    status,
    category,
    search,
  } = useExcelenciaFiltersStore();

  const { data, total, loading, isFetching, error, refetch } = useBlueprintGridQuery();

  useEffect(() => {
    setOrganization(organizationId);
  }, [organizationId, setOrganization]);

  useEffect(() => {
    onItemsChange?.(data);
  }, [data, onItemsChange]);

  useEffect(() => {
    if (!organizationId) return;
    void refetch();
  }, [organizationId, refreshToken, refetch]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleResetFilters = useCallback(() => {
    setSearch('');
    setStatus('all');
    setCategory('all');
    setSort('updated_at', 'desc');
    setPage(1);
  }, [setCategory, setPage, setSearch, setSort, setStatus]);

  const renderSortIcon = (key: typeof sortKey) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? (
      <ArrowUp size={11} className="enterprise-text-primary" />
    ) : (
      <ArrowDown size={11} className="enterprise-text-primary" />
    );
  };

  return (
    <div className="bg-transparent">
      {showFilters ? (
        <div className="flex flex-wrap items-center gap-2 px-2 py-1 border-b border-zinc-800/50">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar blueprint"
            className="h-8 rounded-[4px] border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as typeof status)}
            className="h-8 rounded-[4px] border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <option value="all">Todos</option>
            <option value="draft">Rascunho</option>
            <option value="published">Publicado</option>
            <option value="standard">Padrao</option>
            <option value="archived">Arquivado</option>
          </select>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value as typeof category)}
            className="h-8 rounded-[4px] border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <option value="all">Todos os tipos</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="enterprise-table-shell overflow-x-auto">
        <table className="min-w-full table-fixed text-[11px]">
          <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
            <tr>
              {COLUMNS.map((column) => {
                const isSortable = column.sortable && column.sortKey;
                const isActive = isSortable && sortKey === column.sortKey;
                return (
                  <th
                    key={column.key}
                    onClick={
                      isSortable
                        ? () =>
                            setSort(
                              column.sortKey!,
                              sortKey === column.sortKey && sortDir === 'asc' ? 'desc' : 'asc',
                            )
                        : undefined
                    }
                    className={`px-3 py-2 font-medium ${
                      isSortable
                        ? `cursor-pointer select-none hover:enterprise-text-primary ${
                            isActive ? 'enterprise-text-primary' : ''
                          }`
                        : ''
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      {column.label}
                      {isSortable && renderSortIcon(column.sortKey!)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : (
              data.map((blueprint) => {
                const isSelected = selectedId === blueprint.id;

                return (
                  <tr
                    key={blueprint.id}
                    onClick={() => onSelect(blueprint)}
                    className={`enterprise-table-row cursor-pointer ${isSelected ? 'is-selected' : ''}`}
                  >
                    <td
                      className={`relative px-3 py-2 align-middle font-semibold enterprise-text-primary ${
                        isSelected
                          ? 'pl-4 before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[#718198] before:shadow-[0_0_6px_rgba(113,129,152,0.28)]'
                          : ''
                      }`}
                    >
                      <span className="block truncate">{blueprint.name}</span>
                    </td>
                    <td className="px-3 py-2 align-middle font-mono text-[10px] enterprise-text-secondary">
                      {blueprint.versionSemver ?? '-'}
                    </td>
                    <td className="truncate px-3 py-2 align-middle enterprise-text-secondary">
                      {formatCategory(blueprint.category)}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <BlueprintStatusBadge status={blueprint.status} />
                    </td>
                    <td className="px-3 py-2 align-middle font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-primary">
                      {blueprint.usage_count ?? 0}
                    </td>
                    <td className="truncate px-3 py-2 align-middle enterprise-text-secondary">
                      {formatAuthor(blueprint)}
                    </td>
                    <td className="px-3 py-2 align-middle enterprise-text-secondary">
                      {formatUpdatedAt(blueprint)}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit ? (
                          <RowActionButton title="Editar blueprint" icon={<FilePenLine size={12} />} onClick={() => onEdit(blueprint)} />
                        ) : null}
                        {onOpenHistory ? (
                          <RowActionButton title="Historico" icon={<FileClock size={12} />} onClick={() => onOpenHistory(blueprint)} />
                        ) : null}
                        {onOpenAudit ? (
                          <RowActionButton title="Auditoria" icon={<ShieldCheck size={12} />} onClick={() => onOpenAudit(blueprint)} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {error && !loading ? (
          <div className="p-8 text-center text-sm">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-2 rounded-[4px] border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/30"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              <span>Tentar novamente</span>
            </button>
          </div>
        ) : null}

        {!loading && !error && data.length === 0 ? (
          <div className="p-8 text-center text-sm">
            <p className="enterprise-text-secondary">Nenhum blueprint encontrado com os filtros atuais.</p>
            <button
              type="button"
              className="mt-3 enterprise-button-secondary inline-flex items-center gap-2 rounded-[4px] px-3 py-1.5 text-xs font-medium"
              onClick={handleResetFilters}
            >
              <span>Limpar filtros</span>
            </button>
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-enterprise-border-strong px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] enterprise-text-secondary">
        <span>PAGINA {page} / {totalPages}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="enterprise-button-secondary rounded-[4px] px-2 py-1 disabled:opacity-50"
            disabled={page === 1}
            onClick={() => setPage(Math.max(1, page - 1))}
          >
            ANTERIOR
          </button>
          <button
            type="button"
            className="enterprise-button-secondary rounded-[4px] px-2 py-1 disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => setPage(Math.min(totalPages, page + 1))}
          >
            PROXIMA
          </button>
          {isFetching ? <span>Atualizando</span> : null}
        </div>
      </div>
    </div>
  );
}

function RowActionButton({
  title,
  icon,
  onClick,
}: {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="rounded-[3px] border border-[var(--border-default)] p-1 text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-surface-3)] hover:text-[var(--text-primary)]"
    >
      {icon}
    </button>
  );
}

function SkeletonRows() {
  const rows = Array.from({ length: 5 });
  return (
    <>
      {rows.map((_, idx) => (
        <tr key={idx} className="enterprise-table-row">
          {COLUMNS.map((column) => (
            <td key={column.key} className="px-3 py-2">
              <div className="h-3 rounded-[2px] enterprise-surface-3" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
